import { DatePipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { StepStatusPipe } from '../../../../../common/pipes/step-status/step-status.pipe';
import { SlaDirective } from '../../../../../common/directives/sla/sla.directive';
import { ParallelStepStatusPipe } from '../../../../../common/pipes/parallel-step-status/parallel-step-status.pipe';
import { SideMenuAssigneeDropdownViewComponent } from '../../side-menu-assignee-dropdown-view/side-menu-assignee-dropdown-view.component';
import { RequestCreatorComponent } from '../../../../../common/components/request-creator/request-creator.component';
import { StepStatusEnum } from '../../../../../common/enums/step-status';
import {
  BypassIconComponent,
  ClockIconComponent,
  HoldIconComponent,
  PlayIconComponent,
  VerticalLineIconComponent,
  WarningIconComponent,
  TimerIconComponent,
} from '@kuiper/icons';
import { LogTypeEnum } from './log-type.enum';
import { Accordion2Component } from '../../../../../common/components/accordion-2/accordion-2.component';
import { RoleTypes } from '../../../../../common/constants/role-types.constant';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { AlertInlineComponent } from '../../../../../../ui-components/lib/alert-inline/alert-inline.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { SanitizeHtmlPipe } from '../../../../../../ui-components/pipes/sanitize-html/sanitize-html.pipe';
import { Status } from '../../../../../common/enums/user-status';
import {
  RequestAssignee,
  RequestDetailsResponse,
  RequestStep,
} from '../../../../../common/models/request-details.model';
import { AssignInfo } from '../../../../../common/models/assignee.model';
import { FEATURE_FLAGS } from '../../../my-inbox/contributed-tasks/contributed-tasks.imports';

@Component({
  selector: 'lib-request-view-details',
  imports: [
    TitleCasePipe,
    Accordion2Component,
    BadgeComponent,
    ParallelStepStatusPipe,
    StepStatusPipe,
    DatePipe,
    SlaDirective,
    TranslocoPipe,
    SideMenuAssigneeDropdownViewComponent,
    BypassIconComponent,
    ClockIconComponent,
    WarningIconComponent,
    RequestCreatorComponent,
    AlertInlineComponent,
    HoldIconComponent,
    PlayIconComponent,
    VerticalLineIconComponent,
    TimerIconComponent,
    SanitizeHtmlPipe,
  ],
  templateUrl: './request-view-details.component.html',
  styleUrl: './request-view-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestViewDetailsComponent {
  Status = Status;
  RoleTypes = RoleTypes;

  private readonly featureFlagsService = inject(FeatureFlagsService);
  protected readonly modalService = inject(ModalServiceService);
  private readonly translocoService = inject(TranslocoService);

  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );

  requestDetailsResponse = input.required<RequestDetailsResponse | undefined>();
  stepStatusEnum = StepStatusEnum;
  isManager = RoleTypes.MANAGER;

  logTypeEnum = LogTypeEnum;

  readonly isScheduleOnceOnlyEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.schedule_a_workflow_to_run_once
    )
  );

  readonly isAutoTriggerAlertShown = computed(() => {
    return !this.requestDetailsResponse()?.data?.creator;
  });

  readonly DELETE_ASSIGNEE_STATUS = {
    value: Status.DELETED,
    label: this.translocoService.translate(
      'request_view_screen_deleted_assignee_status_label'
    ),
  };

  readonly systemGeneratedActionTypes = [LogTypeEnum.COLLABORATOR];

  // Method to map assignees to their data
  getMappedAssignees(step: RequestStep): AssignInfo[] {
    return (
      step.details?.assignees?.map((assignee: RequestAssignee) => {
        const deleted_at = assignee.data?.deleted_at ?? null;
        const state = deleted_at
          ? this.DELETE_ASSIGNEE_STATUS
          : (assignee.data?.state ?? null);

        return {
          id: assignee.data?.id,
          name: assignee.data?.name,
          email: assignee.data?.email,
          image: assignee.data?.image ?? undefined,
          state,
          assigneeRole: assignee.assignee_role ?? null,
          deleted_at,
        };
      }) ?? []
    );
  }

  // Method to get assignees length safely
  getAssigneesLength(step: RequestStep): number {
    return step.details?.assignees?.length ?? 0;
  }

  getAssigneeType(step: RequestStep): string[] {
    return (
      step.details?.assignees?.map(
        (assignee: RequestAssignee) => assignee.type
      ) ?? []
    );
  }

  // Check if content contains HTML tags
  isHtml(content: string): boolean {
    if (!content || typeof content !== 'string') {
      return false;
    }
    return /<[^>]+>/g.test(content);
  }
}
